import type { ModuleId, RequiredLevel } from '@qlk/shared';
import type { ReactNode } from 'react';
import { Navigate, useLocation } from 'react-router-dom';

import { AppShell } from '../layout/AppShell.js';
import { KhongDuQuyenPage } from '../modules/core/pages/LoiPages.js';
import { TrangBat2fa } from '../modules/core/pages/TrangBat2fa.js';
import { LoiApi } from './api.js';
import { coQuyen, useToi } from './auth.js';

function DangTaiToanTrang() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-nen">
      <span
        role="status"
        aria-label="Đang tải"
        className="size-8 animate-spin rounded-full border-2 border-vien border-t-nhan"
      />
    </div>
  );
}

/** Bọc toàn bộ phần cần đăng nhập: tải `me`, dựng khung, chặn người chưa đăng nhập. */
export function CanDangNhap({ children }: { children: ReactNode }) {
  const toi = useToi();
  const viTri = useLocation();

  if (toi.isPending) return <DangTaiToanTrang />;

  if (toi.error) {
    const chuaDangNhap = toi.error instanceof LoiApi && toi.error.status === 401;
    if (chuaDangNhap) {
      return <Navigate to="/dang-nhap" replace state={{ tuDau: viTri.pathname }} />;
    }
    return (
      <div className="flex min-h-dvh items-center justify-center bg-nen px-4">
        <p role="alert" className="rounded-the bg-bad-nen px-4 py-3 text-co-ban text-bad-chu">
          Không kết nối được máy chủ: {toi.error.message}
        </p>
      </div>
    );
  }

  if (!toi.data) return <Navigate to="/dang-nhap" replace />;

  /*
   * Bắt buộc bật 2FA mà chưa bật ⇒ **thay toàn bộ** nội dung app bằng màn ép
   * bật, không dựng `AppShell`.
   *
   * Một cờ duy nhất từ máy chủ (`buocBat2fa` của `/core/me`), không tự suy lại
   * từ quyền: luật nằm ở `@qlk/shared/haiBuoc.ts` và máy chủ đã áp nó cùng với
   * điều kiện môi trường. Giao diện chỉ obey.
   *
   * Đây là lớp **thứ hai**: máy chủ đã trả 403 `CAN_2FA` cho mọi đường ngoài
   * danh sách cho qua (`lib/epHaiBuoc.ts`). Màn này để người dùng thấy *vì sao*
   * và *làm gì*, chứ không phải một dãy khung đỏ.
   */
  if (toi.data.buocBat2fa) return <TrangBat2fa batBuoc />;

  return <AppShell me={toi.data}>{children}</AppShell>;
}

/** Chặn theo quyền của một module. Dùng cho từng route. */
export function CanQuyen({
  module,
  level = 'view',
  children,
}: {
  module: ModuleId;
  level?: RequiredLevel;
  children: ReactNode;
}) {
  const toi = useToi();
  if (!toi.data) return null;
  if (!coQuyen(toi.data.permissions, module, level)) return <KhongDuQuyenPage />;
  return <>{children}</>;
}

/**
 * Chuyển hướng một đường dẫn **cũ** sang đường dẫn mới, giữ nguyên query.
 *
 * Bước 3/3 gộp sáu trang thành hai trang có tab, nhưng link và dấu trang cũ
 * (`/nhom-doi-ngu`, `/ket-noi-api`…) vẫn phải mở được — cả cảnh báo của module
 * 11 cũng đang trỏ vào chúng. `replace` để nút Lùi không kẹt ở đường dẫn cũ.
 */
export function ChuyenHuong({ den }: { den: string }) {
  const viTri = useLocation();
  const [duongDan, truyVanCuaDich] = den.split('?');

  /* Query của đích đi trước, query đang có ghi đè lên nếu trùng khoá. */
  const tham = new URLSearchParams(truyVanCuaDich ?? '');
  for (const [khoa, giaTri] of new URLSearchParams(viTri.search)) tham.set(khoa, giaTri);

  const truyVan = tham.toString();
  return <Navigate to={`${duongDan ?? '/'}${truyVan === '' ? '' : `?${truyVan}`}`} replace />;
}
